import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { getColor } from "../../utils/colorfunctions";
import {
    MAJOR_CANNABINOIDS,
    MINOR_CANNABINOIDS,
    SYNTHETIC_CANNABINOIDS,
    ALL_TERPENES,
} from "../COA/coaFieldConfig";
import PdfModal from "./PdfModal";
import "./ProductInfoPage.css";

const publicClient = axios.create({ baseURL: "" });

const filterEntries = (fields, row) =>
    fields
        .map(({ key, label }) => ({ key, label, value: parseFloat(row[key]) || 0 }))
        .filter(({ value }) => value > 0);

const formatDate = (dateStr) => {
    if (!dateStr || dateStr.startsWith("1970-01-01")) return null;
    return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
};

const TYPE_DESCRIPTIONS = {
    "Type I": "THC-dominant",
    "Type II": "Balanced THC & CBD",
    "Type III": "CBD-dominant",
};

// ---------------------------------------------------------------------------
// ProfileBar — CSS-based horizontal bar (no chart.js canvas)
// ---------------------------------------------------------------------------
function ProfileBar({ label, value, maxValue, color, index }) {
    const pct = maxValue > 0 ? (value / maxValue) * 100 : 0;
    return (
        <div className="pi-bar">
            <div className="pi-bar__info">
                <span className="pi-bar__label">{label}</span>
                <span className="pi-bar__value">{value.toFixed(2)}%</span>
            </div>
            <div className="pi-bar__track">
                <div
                    className="pi-bar__fill"
                    style={{
                        width: `${pct}%`,
                        backgroundColor: color || "var(--color-accent)",
                        animationDelay: `${index * 30}ms`,
                    }}
                />
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// SafetyCard — pass / fail / not-tested badge for each safety panel
// ---------------------------------------------------------------------------
function SafetyCard({ label, value, isMoisture }) {
    if (isMoisture) {
        const m = value != null ? parseFloat(value) : NaN;
        const hasData = !isNaN(m);
        return (
            <div className={`pi-safety-card ${hasData ? "pi-safety-card--pass" : "pi-safety-card--neutral"}`}>
                <span className={`pi-safety-card__icon ${hasData ? "pi-safety-card__icon--pass" : "pi-safety-card__icon--neutral"}`}>
                    {hasData ? "✓" : "—"}
                </span>
                <span className="pi-safety-card__label">{label}</span>
                {hasData
                    ? <span className="pi-safety-card__detail">{m.toFixed(2)}%</span>
                    : <span className="pi-safety-card__status pi-safety-card__status--neutral">Not Tested</span>}
            </div>
        );
    }

    const isPass = typeof value === "string" && value.toLowerCase().includes("pass");
    const isFail = typeof value === "string" && value.toLowerCase().includes("fail");
    const status = isPass ? "pass" : isFail ? "fail" : "neutral";
    const statusLabel = isPass ? "Pass" : isFail ? "Fail" : "Not Tested";

    return (
        <div className={`pi-safety-card pi-safety-card--${status}`}>
            <span className={`pi-safety-card__icon pi-safety-card__icon--${status}`}>
                {isPass ? "✓" : isFail ? "✗" : "—"}
            </span>
            <span className="pi-safety-card__label">{label}</span>
            <span className={`pi-safety-card__status pi-safety-card__status--${status}`}>{statusLabel}</span>
        </div>
    );
}

// ---------------------------------------------------------------------------
// ProductInfoPage — public, mobile-first product chemotype & testing view
// ---------------------------------------------------------------------------
export default function ProductInfoPage() {
    const { companySlug, coaId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const preview = location.state?.coa || null;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pdfOpen, setPdfOpen] = useState(false);

    useEffect(() => {
        document.title = preview?.product_name
            ? `${preview.product_name} — Product Info`
            : "Product Info — DYC";
        return () => { document.title = "DYC Terminal"; };
    }, [preview?.product_name]);

    useEffect(() => {
        if (!companySlug || !coaId) return;
        setLoading(true);
        publicClient
            .get(`/public/compliance/${companySlug}/product/${coaId}`)
            .then((res) => {
                setData(res.data);
                document.title = `${res.data.product_name} — Product Info`;
            })
            .catch((err) => {
                setError(
                    err.response?.status === 404
                        ? "Product not found."
                        : "Something went wrong. Please try again later."
                );
            })
            .finally(() => setLoading(false));
    }, [companySlug, coaId]);

    const row = data;

    const pdfUrls = useMemo(() => (row ? [...new Set([
        ...(row.coapdf ? [row.coapdf] : []),
        ...(row.pdf_links?.split("|").filter(Boolean) || []),
    ])] : []), [row]);

    const majorEntries = useMemo(() => (row ? filterEntries(MAJOR_CANNABINOIDS, row) : []), [row]);
    const minorEntries = useMemo(() => (row ? filterEntries(MINOR_CANNABINOIDS, row) : []), [row]);
    const syntheticEntries = useMemo(() => (row ? filterEntries(SYNTHETIC_CANNABINOIDS, row) : []), [row]);
    const allCannabinoidEntries = useMemo(() => [...majorEntries, ...minorEntries], [majorEntries, minorEntries]);
    const terpeneEntries = useMemo(() => (row ? filterEntries(ALL_TERPENES, row) : []), [row]);

    const metrics = useMemo(() => {
        if (!row) return null;
        const totalTHC = ((parseFloat(row.THCa) || 0) * 0.877 + (parseFloat(row.delta_9_THC) || 0)).toFixed(2);
        const totalCBD = ((parseFloat(row.CBDA) || 0) * 0.877 + (parseFloat(row.CBD) || 0)).toFixed(2);
        const totalCannabinoids = allCannabinoidEntries.reduce((sum, e) => sum + e.value, 0).toFixed(2);
        const totalTerpenes = terpeneEntries.reduce((sum, e) => sum + e.value, 0).toFixed(2);
        return {
            thc: (row.THCa == null && row.delta_9_THC == null) ? "N/A" : `${totalTHC}%`,
            cbd: (row.CBDA == null && row.CBD == null) ? "N/A" : `${totalCBD}%`,
            cannabinoids: allCannabinoidEntries.length === 0 ? "N/A" : `${totalCannabinoids}%`,
            terpenes: terpeneEntries.length === 0 ? "N/A" : `${totalTerpenes}%`,
        };
    }, [row, allCannabinoidEntries, terpeneEntries]);

    const accentColor = row ? (() => { const pt = (row.product_type || "").toLowerCase(); return (pt === "flower" || pt === "pre-roll") ? getColor((row.color || "").toLowerCase()) : getColor("no color"); })() : null;

    const maxCannabinoid = useMemo(
        () => allCannabinoidEntries.reduce((max, e) => Math.max(max, e.value), 0),
        [allCannabinoidEntries]
    );
    const maxTerpene = useMemo(
        () => terpeneEntries.reduce((max, e) => Math.max(max, e.value), 0),
        [terpeneEntries]
    );

    const sortedCannabinoids = useMemo(
        () => [...allCannabinoidEntries].sort((a, b) => b.value - a.value),
        [allCannabinoidEntries]
    );
    const sortedTerpenes = useMemo(
        () => [...terpeneEntries].sort((a, b) => b.value - a.value),
        [terpeneEntries]
    );

    const metaRows = useMemo(() => {
        if (!row) return [];
        const META_FIELDS = [
            { key: "lab_name", label: "Laboratory" },
            { key: "lab_state", label: "Lab State" },
            { key: "sample_id", label: "Sample ID" },
            { key: "batch_id", label: "Batch ID" },
            { key: "date_collected", label: "Date Collected", isDate: true },
            { key: "date_received", label: "Date Received", isDate: true },
            { key: "date_completed", label: "Date Completed", isDate: true },
            { key: "cultivator_producer_name", label: "Cultivator / Producer" },
            { key: "distributor_name", label: "Distributor" },
        ];
        return META_FIELDS
            .map(({ key, label, isDate }) => {
                const raw = row[key];
                if (!raw) return null;
                const value = isDate ? formatDate(raw) : raw;
                if (!value) return null;
                return { label, value };
            })
            .filter(Boolean);
    }, [row]);

    // ── Loading state (show preview header while full data fetches) ──
    if (loading) {
        return (
            <div className="pi-page">
                <button className="pi-back" onClick={() => navigate(-1)}>
                    &larr; Back
                </button>
                {preview && (
                    <div className="pi-header">
                        <h1 className="pi-header__name">{preview.product_name}</h1>
                    </div>
                )}
                <div className="pi-loading">
                    <div className="pi-spinner" />
                    <span>Loading product info&hellip;</span>
                </div>
            </div>
        );
    }

    // ── Error state ──
    if (error) {
        return (
            <div className="pi-page">
                <button className="pi-back" onClick={() => navigate(-1)}>
                    &larr; Back
                </button>
                <div className="pi-error">{error}</div>
            </div>
        );
    }

    if (!row) return null;

    return (
        <div className="pi-page">
            {/* ── Back navigation + COA files ── */}
            <div className="pi-topbar">
                <button className="pi-back" onClick={() => navigate(-1)}>
                    &larr; Back
                </button>
                {pdfUrls.length > 0 && (
                    <button className="pi-view-files-btn" onClick={() => setPdfOpen(true)}>
                        View COA Files
                    </button>
                )}
            </div>

            {/* ── Product header ── */}
            <div className="pi-header">
                <div className="pi-header__top">
                    <img className="pi-header__icon" src="/DYC-icon-ColorWheel.png" alt="DYC" />
                    <div className="pi-header__title-group">
                        <h1 className="pi-header__name">{row.product_name}</h1>
                        <div className="pi-header__tags">
                            {row.cannabinoid_type && (
                                <span className="pi-header__type-badge">
                                    {row.cannabinoid_type}
                                    {TYPE_DESCRIPTIONS[row.cannabinoid_type] && (
                                        <span className="pi-header__type-desc">
                                            {" — "}{TYPE_DESCRIPTIONS[row.cannabinoid_type]}
                                        </span>
                                    )}
                                </span>
                            )}
                            {row.dycId && <span className="pi-header__dyc-id">{row.dycId}</span>}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Key metrics (2x2 grid) ── */}
            {metrics && (
                <div className="pi-metrics">
                    <div className="pi-metric-card">
                        <div className="pi-metric-card__value">{metrics.thc}</div>
                        <div className="pi-metric-card__label">Total THC</div>
                    </div>
                    <div className="pi-metric-card">
                        <div className="pi-metric-card__value">{metrics.cbd}</div>
                        <div className="pi-metric-card__label">Total CBD</div>
                    </div>
                    <div className="pi-metric-card">
                        <div className="pi-metric-card__value">{metrics.cannabinoids}</div>
                        <div className="pi-metric-card__label">Total Cannabinoids</div>
                    </div>
                    <div className="pi-metric-card">
                        <div className="pi-metric-card__value">{metrics.terpenes}</div>
                        <div className="pi-metric-card__label">Total Terpenes</div>
                    </div>
                </div>
            )}

            {/* ── Safety testing ── */}
            <div className="pi-section">
                <div className="pi-section__header">
                    <h2 className="pi-section__title">Safety Testing</h2>
                    <p className="pi-section__subtitle">Verified by an independent licensed laboratory</p>
                </div>
                <div className="pi-safety-grid">
                    <SafetyCard label="Microbials" value={row.microbials} />
                    <SafetyCard label="Pesticides" value={row.pesticides} />
                    <SafetyCard label="Heavy Metals" value={row.heavy_metals} />
                    <SafetyCard label="Foreign Matter" value={row.foreign_matter} />
                    <SafetyCard label="Mycotoxins" value={row.mycotoxins} />
                    <SafetyCard label="Moisture" value={row.moisture_content} isMoisture />
                </div>
            </div>

            {/* ── Synthetic cannabinoid warning ── */}
            {syntheticEntries.length > 0 && (
                <div className="pi-warning">
                    <div className="pi-warning__icon">{"⚠"}</div>
                    <div className="pi-warning__content">
                        <strong>Synthetic Cannabinoids Detected</strong>
                        <p>This product tested positive for synthetic cannabinoids, which may pose health risks.</p>
                        <div className="pi-warning__compounds">
                            {syntheticEntries.map((e) => (
                                <span key={e.key} className="pi-warning__compound">
                                    {e.label}: {e.value.toFixed(4)}%
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Cannabinoid profile ── */}
            {sortedCannabinoids.length > 0 && (
                <div className="pi-section">
                    <div className="pi-section__header">
                        <h2 className="pi-section__title">Cannabinoid Profile</h2>
                        <p className="pi-section__subtitle">Active compounds and their concentrations</p>
                    </div>
                    <div className="pi-bars">
                        {sortedCannabinoids.map((e, i) => (
                            <ProfileBar
                                key={e.key}
                                label={e.label}
                                value={e.value}
                                maxValue={maxCannabinoid}
                                color={accentColor}
                                index={i}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* ── Terpene profile ── */}
            <div className="pi-section">
                <div className="pi-section__header">
                    <h2 className="pi-section__title">Terpene Profile</h2>
                    <p className="pi-section__subtitle">Compounds that influence flavor, aroma, and effects</p>
                </div>
                {sortedTerpenes.length > 0 ? (
                    <div className="pi-bars">
                        {sortedTerpenes.map((e, i) => (
                            <ProfileBar
                                key={e.key}
                                label={e.label}
                                value={e.value}
                                maxValue={maxTerpene}
                                color={accentColor || "#9e9e9e"}
                                index={i}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="pi-no-data">No terpene data available for this product.</div>
                )}
            </div>

            {/* ── Lab verification ── */}
            {metaRows.length > 0 && (
                <div className="pi-section">
                    <div className="pi-section__header">
                        <h2 className="pi-section__title">Lab Verification</h2>
                        <p className="pi-section__subtitle">Testing details and traceability</p>
                    </div>
                    <div className="pi-meta-list">
                        {metaRows.map(({ label, value }) => (
                            <div key={label} className="pi-meta-row">
                                <span className="pi-meta-row__label">{label}</span>
                                <span className="pi-meta-row__value">{value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <footer className="pi-footer">
                <span>Powered by DYC &mdash; Designate Your Chemotype</span>
            </footer>

            {pdfOpen && <PdfModal urls={pdfUrls} onClose={() => setPdfOpen(false)} />}
        </div>
    );
}
